"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import SearchableSelect from "@/components/ui/SearchableSelect";
import DatePicker from "@/components/form/date-picker";
import Pagination from "@/components/tables/Pagination";
import { Modal } from "@/components/ui/modal/index";
import PurchaseChallanPrint from "@/components/print/PurchaseChallanPrint";
import PurchaseChallansListPrint from "@/components/print/PurchaseChallansListPrint";
import { rmcClient } from "@/lib/rmcClient";
import { roundMoney, roundToDecimals } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import toast from "react-hot-toast";
import React from "react";

function formatNumber(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (digits === 2) return roundMoney(n).toFixed(2);
  if (digits === 3) return roundToDecimals(n, 3).toFixed(3);
  return n.toFixed(digits);
}

function partyName(row, parties) {
  if (row?.party?.name) return row.party.name;
  const id = row?.party_id ?? row?.party?.id;
  return parties.find((p) => p.id === id)?.name ?? "—";
}

function materialName(row, materials) {
  if (Array.isArray(row?.items) && row.items.length) {
    return row.items.map((it) => it?.material?.name).filter(Boolean).join(", ");
  }
  if (row?.material?.name) return row.material.name;
  const id = row?.material_id ?? row?.material?.id;
  return materials.find((m) => m.id === id)?.name ?? "—";
}

function challanQty(row) {
  const items = Array.isArray(row?.items) ? row.items : [];
  if (items.length) {
    const total = items.reduce((sum, it) => {
      const qty = Number(it?.qty);
      return Number.isFinite(qty) ? sum + qty : sum;
    }, 0);
    if (Number.isFinite(total) && total > 0) return total;
  }
  const net = Number(row?.qty ?? row?.net_weight);
  if (Number.isFinite(net)) return net;
  return NaN;
}

function computePurchaseChallanTotals(rows) {
  let qty = 0;
  let amount = 0;
  for (const r of rows) {
    const q = challanQty(r);
    if (Number.isFinite(q)) qty += q;
    const a = challanAmount(r);
    if (Number.isFinite(a)) amount += a;
  }
  return {
    count: rows.length,
    qty: roundToDecimals(qty, 3),
    amount: roundMoney(amount),
  };
}

function challanAmount(row) {
  if (row?.total_amount != null && row.total_amount !== "") {
    const t = Number(row.total_amount);
    if (Number.isFinite(t)) return t;
  }
  if (row?.sub_total != null && row.sub_total !== "") {
    const s = Number(row.sub_total);
    if (Number.isFinite(s)) return s;
  }
  if (row?.amount != null && row.amount !== "") {
    const a = Number(row.amount);
    if (Number.isFinite(a)) return a;
  }
  const net = Number(row?.qty ?? row?.net_weight);
  const rate = Number(row?.rate);
  if (Number.isFinite(net) && Number.isFinite(rate)) return net * rate;
  return NaN;
}

/** Normalized view + print payload */
function buildPurchaseChallanDetail(row, parties, materials) {
  if (!row) return null;
  const amt = challanAmount(row);
  const qtyVal = challanQty(row);
  const partyId = row?.party_id ?? row?.party?.id;
  const partyObj = row?.party || parties.find((p) => p.id === partyId) || null;
  return {
    id: row.id ?? null,
    challan_no: row.challan_no ?? "—",
    date: row.date ?? "—",
    party: partyName(row, parties),
    party_details: partyObj,
    material: materialName(row, materials),
    uom: row.material?.uom ?? "TON",
    qty: Number.isFinite(qtyVal) ? qtyVal : (row.qty ?? row.net_weight),
    rate: row.rate,
    amount: Number.isFinite(amt) ? amt : null,
    items: Array.isArray(row.items) ? row.items : [],
    tax_type: row.tax_type ?? "",
    gst_rate: row.gst_rate ?? "",
    gst_amount: row.gst_amount ?? "",
    sub_total: row.sub_total ?? "",
    total_amount: row.total_amount ?? "",
    vehicle_no: row.vehicle_no ?? "—",
    remark: row.remark ?? "",
    purchase_invoice_no: row.purchase_invoice_no ?? "",
    kata_details: row.kata_details ?? null,
    net_weight: row.net_weight ?? "",
    purchase_kind: row.purchase_kind ?? "REGULAR",
    royalty_no: row.royalty_no ?? "",
    concrete_quarry_name:
      row.concrete_quarry?.name ?? row.concrete_quarry_name ?? row.quarry?.name ?? "",
  };
}

const PAGE_SIZE = 20;

export default function RmcPurchaseChallansList() {
  const qc = useQueryClient();
  const isoToday = React.useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);
  const [row, setRow] = React.useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = React.useState(null);
  const [filters, setFilters] = React.useState({
    party_id: "",
    material_id: "",
    purchase_kind: "",
    date_from: isoToday,
    date_to: isoToday,
  });
  const [page, setPage] = React.useState(1);
  const [isExporting, setIsExporting] = React.useState(false);
  const [pdfBusy, setPdfBusy] = React.useState(false);
  const printRef = React.useRef(null);
  const listPrintRef = React.useRef(null);
  const { data: parties = [] } = useQuery({
    queryKey: ["purchaseParties"],
    queryFn: async () => (await rmcClient.listPurchaseParties()).data,
  });
  const { data: materials = [] } = useQuery({
    queryKey: ["materials"],
    queryFn: async () => (await rmcClient.listMaterials()).data,
  });
  const queryParams = React.useMemo(
    () => ({
      ...(filters.party_id ? { party_id: filters.party_id } : {}),
      ...(filters.material_id ? { material_id: filters.material_id } : {}),
      ...(filters.purchase_kind ? { purchase_kind: filters.purchase_kind } : {}),
      ...(filters.date_from ? { date_from: filters.date_from } : {}),
      ...(filters.date_to ? { date_to: filters.date_to } : {}),
      page,
      page_size: PAGE_SIZE,
    }),
    [filters, page]
  );
  const { data: challansData, isFetching } = useQuery({
    queryKey: ["purchaseChallans", queryParams],
    queryFn: async () => (await rmcClient.listPurchaseChallans(queryParams)).data,
    keepPreviousData: true,
  });
  const rows = React.useMemo(() => {
    if (Array.isArray(challansData)) return challansData;
    if (Array.isArray(challansData?.results)) return challansData.results;
    if (Array.isArray(challansData?.data?.results)) return challansData.data.results;
    return [];
  }, [challansData]);
  const listResponseMeta = React.useMemo(() => {
    if (!challansData || typeof challansData !== "object" || Array.isArray(challansData)) return null;
    const m = challansData.meta ?? challansData.data?.meta;
    return m && typeof m === "object" ? m : null;
  }, [challansData]);

  const filterParams = React.useMemo(
    () => ({
      ...(filters.party_id ? { party_id: filters.party_id } : {}),
      ...(filters.material_id ? { material_id: filters.material_id } : {}),
      ...(filters.purchase_kind ? { purchase_kind: filters.purchase_kind } : {}),
      ...(filters.date_from ? { date_from: filters.date_from } : {}),
      ...(filters.date_to ? { date_to: filters.date_to } : {}),
    }),
    [filters]
  );

  const { data: filteredTotals } = useQuery({
    queryKey: ["purchaseChallansTotals", filterParams],
    queryFn: async () => {
      const payload = (
        await rmcClient.listPurchaseChallans({ ...filterParams, pagination: false })
      ).data;
      const allRows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
          ? payload.results
          : [];
      return computePurchaseChallanTotals(allRows);
    },
    staleTime: 30_000,
  });

  const summaryTotals = React.useMemo(() => {
    const metaQty = Number(listResponseMeta?.total_qty ?? listResponseMeta?.total_qty_ton);
    const metaAmt = Number(
      listResponseMeta?.total_amount ?? listResponseMeta?.total_sub_total ?? listResponseMeta?.total
    );
    const metaCount = Number(challansData?.count ?? challansData?.data?.count);
    if (filteredTotals) {
      return {
        count: Number.isFinite(metaCount) && metaCount >= 0 ? metaCount : filteredTotals.count,
        qty: Number.isFinite(metaQty) && metaQty >= 0 ? metaQty : filteredTotals.qty,
        amount: Number.isFinite(metaAmt) && metaAmt >= 0 ? metaAmt : filteredTotals.amount,
      };
    }
    return {
      count: Number.isFinite(metaCount) && metaCount >= 0 ? metaCount : 0,
      qty: Number.isFinite(metaQty) && metaQty >= 0 ? metaQty : null,
      amount: Number.isFinite(metaAmt) && metaAmt >= 0 ? metaAmt : null,
    };
  }, [listResponseMeta, challansData, filteredTotals]);

  const totalCount = React.useMemo(() => {
    const value = Number(challansData?.count);
    if (Number.isFinite(value) && value >= 0) return value;
    const nestedValue = Number(challansData?.data?.count);
    if (Number.isFinite(nestedValue) && nestedValue >= 0) return nestedValue;
    return rows.length;
  }, [challansData, rows.length]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const updateFilters = React.useCallback((next) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  }, []);

  const clearFilters = React.useCallback(() => {
    setFilters({
      party_id: "",
      material_id: "",
      purchase_kind: "",
      date_from: isoToday,
      date_to: isoToday,
    });
    setPage(1);
  }, [isoToday]);

  const exportPurchaseChallansPdf = React.useCallback(async () => {
    setPdfBusy(true);
    try {
      await listPrintRef.current?.openPrint?.({
        filters,
        parties,
        materials,
      });
    } catch {
      toast.error("Could not prepare PDF export");
    } finally {
      setPdfBusy(false);
    }
  }, [filters, materials, parties]);

  const exportFiltered = React.useCallback(async () => {
    setIsExporting(true);
    try {
      const payload = (
        await rmcClient.listPurchaseChallans({ ...filterParams, pagination: false })
      ).data;
      const exportRows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
          ? payload.results
          : [];
      const totals = computePurchaseChallanTotals(exportRows);
      const header = [
        "challan_no",
        "date",
        "party",
        "material",
        "purchase_kind",
        "qty",
        "rate",
        "amount",
        "vehicle_no",
        "remark",
      ];
      const lines = exportRows.map((r) => {
        const qty = challanQty(r);
        const amt = challanAmount(r);
        const values = [
          r.challan_no ?? "",
          r.date ?? "",
          partyName(r, parties),
          materialName(r, materials),
          r.purchase_kind ?? "REGULAR",
          Number.isFinite(qty) ? roundToDecimals(qty, 3) : "",
          r.rate ?? "",
          Number.isFinite(amt) ? roundMoney(amt) : "",
          r.vehicle_no ?? "",
          r.remark ?? "",
        ];
        return values
          .map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`)
          .join(",");
      });
      const summaryRow = [
        "",
        "",
        "",
        "",
        "TOTALS",
        totals.qty,
        "",
        totals.amount,
        "",
        `${totals.count} challan(s)`,
      ]
        .map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`)
        .join(",");
      const csv = [header.join(","), ...lines, "", summaryRow].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `purchase-challans-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${exportRows.length} challans`);
    } catch {
      toast.error("Failed to export challans.");
    } finally {
      setIsExporting(false);
    }
  }, [filterParams, materials, parties]);
  const detail = React.useMemo(
    () => (row ? buildPurchaseChallanDetail(row, parties, materials) : null),
    [row, parties, materials]
  );
  const activeFilterCount = React.useMemo(
    () =>
      [filters.party_id, filters.material_id, filters.purchase_kind, filters.date_from, filters.date_to].filter(
        Boolean
      ).length,
    [filters]
  );

  const remove = useMutation({
    mutationFn: (id) => rmcClient.deletePurchaseChallan(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["purchaseChallans"] });
      qc.invalidateQueries({ queryKey: ["purchaseChallansTotals"] });
      qc.invalidateQueries({ queryKey: ["materials"] });
      qc.removeQueries({ queryKey: ["purchaseChallan", id] });
      toast.success("Challan deleted");
      setRow((r) => (r?.id === id ? null : r));
      setDeleteConfirmId(null);
    },
    onError: () => toast.error("Failed to delete challan."),
  });

  const deleteTarget = React.useMemo(
    () => (deleteConfirmId != null ? rows.find((r) => r.id === deleteConfirmId) : null),
    [deleteConfirmId, rows]
  );

  const requestDelete = (id) => setDeleteConfirmId(id);

  const confirmDelete = () => {
    if (deleteConfirmId == null) return;
    remove.mutate(deleteConfirmId);
  };

  const showSummaryCards =
    summaryTotals.qty != null || summaryTotals.amount != null || summaryTotals.count > 0;

  return (
    <div className="space-y-6">
      <PurchaseChallansListPrint ref={listPrintRef} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageBreadCrumb pageTitle="Purchase Challans" />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={pdfBusy || isExporting}
            onClick={exportPurchaseChallansPdf}
          >
            {pdfBusy ? "Preparing…" : "Export PDF"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isExporting || pdfBusy}
            onClick={exportFiltered}
          >
            {isExporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Link
            href="/purchase/challans/new"
            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-1.5 text-xs font-medium text-black shadow-theme-xs hover:bg-gray-200"
          >
            New
          </Link>
        </div>
      </div>
      <div className="rounded-2xl border border-gray-200/80 bg-white/90 p-4 shadow-lg shadow-gray-200/50 ring-1 ring-gray-100/80 backdrop-blur-sm transition-all dark:border-gray-800 dark:bg-gray-900/50 dark:shadow-black/30 dark:ring-gray-800/80">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Smart Filters
            </p>
            {activeFilterCount > 0 && (
              <span className="menu-dropdown-badge-active rounded-full px-2.5 py-1 text-[11px] font-semibold">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing {rows.length} of {totalCount} records
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date From</label>
            <DatePicker
              id="purchase-challan-filter-from"
              defaultDate={filters.date_from}
              placeholder="From date"
              onChange={(_, dateStr) => updateFilters({ date_from: dateStr || "" })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date To</label>
            <DatePicker
              id="purchase-challan-filter-to"
              defaultDate={filters.date_to}
              placeholder="To date"
              onChange={(_, dateStr) => updateFilters({ date_to: dateStr || "" })}
            />
          </div>
          <SearchableSelect
            label="Party"
            value={filters.party_id}
            onChange={(v) => updateFilters({ party_id: v || "" })}
            options={[{ id: "", name: "All parties" }, ...parties]}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
          <SearchableSelect
            label="Material"
            value={filters.material_id}
            onChange={(v) => updateFilters({ material_id: v || "" })}
            options={[{ id: "", name: "All materials" }, ...materials]}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
          <SearchableSelect
            label="Purchase kind"
            value={filters.purchase_kind === "" ? undefined : filters.purchase_kind}
            onChange={(v) => updateFilters({ purchase_kind: v ? String(v) : "" })}
            options={[
              { id: "", name: "All kinds" },
              { id: "REGULAR", name: "Regular" },
              { id: "CONCRETE", name: "Concrete" },
            ]}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200/70 pt-3 dark:border-gray-800">
          <Button type="button" size="sm" variant="outline" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      </div>

      {showSummaryCards && (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-gray-200/80 bg-white/90 px-5 py-4 shadow-lg shadow-gray-200/50 ring-1 ring-gray-100/80 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/50 dark:shadow-black/30 dark:ring-gray-800/80">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
              Challans
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-gray-900 dark:text-white">
              {summaryTotals.count}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200/80 bg-white/90 px-5 py-4 shadow-lg shadow-gray-200/50 ring-1 ring-gray-100/80 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/50 dark:shadow-black/30 dark:ring-gray-800/80">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
              Total quantity
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-gray-900 dark:text-white">
              {summaryTotals.qty != null ? formatNumber(summaryTotals.qty, 3) : "—"}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200/80 bg-white/90 px-5 py-4 shadow-lg shadow-gray-200/50 ring-1 ring-gray-100/80 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/50 dark:shadow-black/30 dark:ring-gray-800/80">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
              Total amount
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-gray-900 dark:text-white">
              {summaryTotals.amount != null ? `₹${formatNumber(summaryTotals.amount, 2)}` : "—"}
            </p>
          </div>
        </div>
      )}

      <DataTable
        columns={[
          { key: "challan_no", header: "Challan No" },
          { key: "date", header: "Date" },
          {
            key: "party",
            header: "Party",
            render: (r) => partyName(r, parties),
          },
          {
            key: "kind",
            header: "Kind",
            render: (r) =>
              r.purchase_kind === "CONCRETE"
                ? "Concrete"
                : r.purchase_kind === "REGULAR"
                  ? "Regular"
                  : r.purchase_kind || "—",
          },
          {
            key: "mat",
            header: "Material",
            render: (r) => materialName(r, materials),
          },
          {
            key: "qty",
            header: "Qty",
            render: (r) => {
              const qty = challanQty(r);
              return Number.isFinite(qty) ? formatNumber(qty, 3) : "—";
            },
          },
          { key: "rate", header: "Rate", render: (r) => (r.rate != null ? `₹${formatNumber(r.rate, 2)}` : "—") },
          { key: "tax", header: "Tax", render: (r) => r.tax_type || "—" },
          {
            key: "amt",
            header: "Amount",
            render: (r) => {
              const a = challanAmount(r);
              return Number.isFinite(a) ? `₹${formatNumber(a, 2)}` : "₹—";
            },
          },
          { key: "veh", header: "Vehicle", render: (r) => r.vehicle_no },
          {
            key: "actions",
            header: "Actions",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (r) => (
              <div className="flex w-full items-center justify-end gap-2">
                <Link
                  href={`/purchase/challans/${r.id}/edit`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
                  aria-label="Edit challan"
                  title="Edit"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
                    <path
                      d="M12.5 3.5l4 4-9 9H6v-3.5l9-9Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M11 5l4 4M4 16.5h4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </Link>
                <button
                  type="button"
                  aria-label="Delete challan"
                  title="Delete"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
                  disabled={remove.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    requestDelete(r.id);
                  }}
                >
                  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
                    <path
                      d="M7.5 3.5h5M4.5 5.5h11M6.5 5.5l.6 9a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-9M8.5 8.5v4.5M11.5 8.5v4.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            ),
          },
        ]}
        data={rows}
        isLoading={isFetching}
        onRowClick={setRow}
      />
      <div className="flex justify-end">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => setPage(Math.max(1, Math.min(totalPages, p)))}
        />
      </div>
      <Modal
        isOpen={!!row}
        onClose={() => setRow(null)}
        className="max-w-3xl w-full max-h-[min(90vh,720px)] overflow-y-auto"
      >
        {row && detail && (
          <div className="space-y-5 pr-2">
            <div className="flex flex-col gap-3 border-b border-gray-800 pb-4 sm:flex-row sm:items-start sm:justify-between sm:pr-8">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Purchase Challan
                </p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight text-white">
                  {detail.challan_no}
                </h2>
                <p className="mt-1 text-sm text-gray-400">Date: {detail.date}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => printRef.current?.openPrint?.()}
                >
                  Print
                </Button>
                <Link
                  href={`/purchase/challans/${row.id}/edit`}
                  onClick={() => setRow(null)}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-xs font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  Edit
                </Link>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => requestDelete(row.id)}
                >
                  Delete
                </Button>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-800">
              <table className="w-full border-collapse text-left text-xs">
                <tbody className="divide-y divide-gray-800">
                  <tr className="bg-gray-950/80">
                    <th className="w-36 px-3 py-2.5 font-semibold text-gray-500">Party</th>
                    <td className="px-3 py-2.5 text-gray-200">{detail.party}</td>
                  </tr>
                  <tr>
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Tax Type</th>
                    <td className="px-3 py-2.5 text-gray-200">
                      {detail.tax_type || "—"}
                      {detail.tax_type === "GST" && detail.gst_rate ? ` (${formatNumber(detail.gst_rate, 2)}%)` : ""}
                    </td>
                  </tr>
                  <tr className="bg-gray-950/40">
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Purchase kind</th>
                    <td className="px-3 py-2.5 text-gray-200">
                      {detail.purchase_kind === "CONCRETE"
                        ? "Concrete"
                        : detail.purchase_kind === "REGULAR"
                          ? "Regular"
                          : detail.purchase_kind || "—"}
                    </td>
                  </tr>
                  {detail.purchase_kind === "CONCRETE" && (
                    <>
                      <tr>
                        <th className="px-3 py-2.5 font-semibold text-gray-500">Quarry</th>
                        <td className="px-3 py-2.5 text-gray-200">
                          {detail.concrete_quarry_name?.trim() ? detail.concrete_quarry_name : "—"}
                        </td>
                      </tr>
                      <tr className="bg-gray-950/40">
                        <th className="px-3 py-2.5 font-semibold text-gray-500">Royalty no.</th>
                        <td className="px-3 py-2.5 font-mono text-sm text-white">
                          {detail.royalty_no?.trim() ? detail.royalty_no : "—"}
                        </td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Vehicle No.</th>
                    <td className="px-3 py-2.5 font-mono text-sm text-white">{detail.vehicle_no}</td>
                  </tr>
                  <tr>
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Purchase invoice no.</th>
                    <td className="px-3 py-2.5 text-gray-200">
                      {detail.purchase_invoice_no?.trim() ? detail.purchase_invoice_no : "—"}
                    </td>
                  </tr>
                  <tr className="bg-gray-950/40">
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Kata (gross / tare)</th>
                    <td className="px-3 py-2.5 text-gray-200">
                      {detail.kata_details &&
                      (detail.kata_details.gross_weight != null || detail.kata_details.tare_weight != null)
                        ? `${formatNumber(detail.kata_details.gross_weight, 3)} / ${formatNumber(detail.kata_details.tare_weight, 3)}`
                        : "—"}
                    </td>
                  </tr>
                  <tr>
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Auto net (kata)</th>
                    <td className="px-3 py-2.5 font-mono text-gray-200">
                      {detail.kata_details?.auto_net_weight != null
                        ? formatNumber(detail.kata_details.auto_net_weight, 3)
                        : "—"}
                    </td>
                  </tr>
                  <tr className="bg-gray-950/40">
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Net weight</th>
                    <td className="px-3 py-2.5 font-mono text-gray-200">
                      {detail.net_weight != null && detail.net_weight !== "" ? formatNumber(detail.net_weight, 3) : "—"}
                    </td>
                  </tr>
                  <tr>
                    <th className="px-3 py-2.5 font-semibold text-gray-500">Remark</th>
                    <td className="px-3 py-2.5 text-gray-400">
                      {detail.remark?.trim() ? detail.remark : "—"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Quantity & Amount
              </p>
              <div className="overflow-x-auto rounded-lg border border-gray-800">
                <table className="w-full min-w-[520px] border-collapse text-center text-xs">
                  <thead>
                    <tr className="border-b border-gray-800 bg-gray-950/90">
                      <th className="px-3 py-2.5 font-semibold text-gray-500">Material</th>
                      <th className="px-3 py-2.5 font-semibold text-gray-500">Qty ({detail.uom})</th>
                      <th className="px-3 py-2.5 font-semibold text-gray-500">Rate (₹)</th>
                      <th className="px-3 py-2.5 font-semibold text-gray-500">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.items.length ? detail.items : [detail]).map((it, idx) => (
                      <tr key={idx}>
                        <td className="border-t border-gray-800 px-3 py-3 text-left text-gray-200">
                          {it?.material?.name || detail.material}
                        </td>
                        <td className="border-t border-gray-800 px-3 py-3 font-medium text-white">
                          {formatNumber(it?.qty ?? detail.qty, 3)}
                        </td>
                        <td className="border-t border-gray-800 px-3 py-3 text-gray-200">
                          ₹{formatNumber(it?.rate ?? detail.rate, 2)}
                        </td>
                        <td className="border-t border-gray-800 px-3 py-3 font-semibold text-white">
                          {it?.amount != null || detail.amount != null
                            ? `₹${formatNumber(it?.amount ?? detail.amount, 2)}`
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 text-right text-sm text-gray-300">
                <p>Sub Total: <span className="font-semibold text-white">₹{formatNumber(detail.sub_total || detail.amount, 2)}</span></p>
                <p>GST: <span className="font-semibold text-white">₹{formatNumber(detail.gst_amount, 2)}</span></p>
                <p>Total: <span className="font-semibold text-white">₹{formatNumber(detail.total_amount || detail.amount, 2)}</span></p>
              </div>
            </div>

            <PurchaseChallanPrint ref={printRef} detail={detail} />
          </div>
        )}
      </Modal>

      <Modal
        isOpen={deleteConfirmId != null}
        onClose={() => {
          if (!remove.isPending) setDeleteConfirmId(null);
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Delete purchase challan?</h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-400">
              {deleteTarget?.challan_no ? (
                <>
                  This will permanently remove{" "}
                  <span className="font-medium text-gray-200">{deleteTarget.challan_no}</span> from
                  the list. Related stock may be adjusted.
                </>
              ) : (
                <>This will remove the challan. Related stock may be adjusted.</>
              )}
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-gray-800 pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={remove.isPending}
              onClick={() => setDeleteConfirmId(null)}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={remove.isPending} onClick={confirmDelete}>
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
